import React, { useId, useState } from 'react';
import { directory } from './manufacturerProducts';
import './ManufacturerProductDirectory.css';

function SourceLink({ id }) {
  const source = directory.sources.find(s => s.id === id);
  return <a href={source.url} target="_blank" rel="noopener noreferrer">{source.title}</a>;
}

function Restrictions({ product }) {
  return <>
    <ul className="manufacturer-notes">{product.notes.map((note, i) => <li key={i}>
      <strong>{note.context}.</strong> {note.text} <SourceLink id={note.source_id} />
    </li>)}</ul>
    <p className="manufacturer-fine">This summary is incomplete. Missing restrictions do not mean there are none; use the exact product’s current IFU.</p>
  </>;
}

function ProductSources({ product }) {
  const [expanded, setExpanded] = useState(false);
  return <details onToggle={event => {
    if (event.target === event.currentTarget) setExpanded(event.currentTarget.open);
  }}>
    <summary>Sources and product restrictions</summary>
    {expanded && <>
      <Restrictions product={product} />
      <ul>{product.source_ids.map(id => {
        const source = directory.sources.find(s => s.id === id);
        return <li key={id}><SourceLink id={id} /><p>{source.revision} · observed {source.observed_on}</p><p>{source.currency_note}</p></li>;
      })}</ul>
    </>}
  </details>;
}

export default function ManufacturerProductDirectory() {
  const headingId = useId();
  const [expanded, setExpanded] = useState(false);
  const [query, setQuery] = useState('');
  const [manufacturer, setManufacturer] = useState('');
  const [selected, setSelected] = useState([]);
  const [comparing, setComparing] = useState(false);
  const matches = directory.search(query, manufacturer);
  const selectedProducts = selected.map(id => directory.products.find(p => p.id === id));
  const comparison = comparing && selected.length >= 2 ? directory.compare(selected) : [];

  function toggle(id) {
    setComparing(false);
    setSelected(current => current.includes(id)
      ? current.filter(value => value !== id)
      : current.length < 4 ? [...current, id] : current);
  }

  return <details className="manufacturer-directory" onToggle={event => {
    if (event.target === event.currentTarget) setExpanded(event.currentTarget.open);
  }}>
    <summary>Explore products · {Object.keys(directory.manufacturer_names).length} manufacturers</summary>
    {expanded && <section aria-labelledby={headingId}>
      <div className="manufacturer-heading">
        <div><h2 id={headingId}>Compare exact product variants</h2>
          <p>Search {directory.products.length} catalog records from {Object.values(directory.manufacturer_names).join(', ')}.</p>
        </div>
        <span className="manufacturer-market">US sources · observed {directory.observed_on}</span>
      </div>
      <p>Compare size, packaging and source-specific restrictions. Matching names or similar materials do not establish clinical interchangeability.</p>
      <div className="manufacturer-filters">
        <label>Find a product or catalog number<input value={query} maxLength={200} placeholder="Try Mepilex, Prisma or 553245" onChange={e => setQuery(e.target.value)} /></label>
        <label>Manufacturer<select value={manufacturer} onChange={e => setManufacturer(e.target.value)}>
          <option value="">All manufacturers</option>
          {Object.entries(directory.manufacturer_names).map(([key, name]) => <option key={key} value={key}>{name}</option>)}
        </select></label>
      </div>
      <div className="manufacturer-selection" role="group" aria-label="Products selected for comparison">
        <p role="status">{matches.length} matching records · {selected.length} of 4 selected</p>
        <ul>{selectedProducts.map(p => <li key={p.id}><button type="button" onClick={() => toggle(p.id)} aria-label={`Remove ${p.sku} from comparison`}>{p.name} · {p.sku} <span aria-hidden="true">×</span></button></li>)}</ul>
        <button type="button" disabled={selected.length < 2} onClick={() => setComparing(true)}>Compare selected products</button>
        {!!selected.length && <button type="button" onClick={() => { setSelected([]); setComparing(false); }}>Clear selection</button>}
      </div>
      {comparison.length > 0 && <div className="manufacturer-comparison" role="region" aria-label="Selected product comparison" tabIndex={0}>
        <table><caption>Exact variant comparison — no substitution or treatment recommendation</caption>
          <thead><tr><th scope="col">Specification</th>{comparison.map(p => <th scope="col" key={p.id}>{p.name}<br />{p.sku}</th>)}</tr></thead>
          <tbody>{[['Manufacturer', p => directory.manufacturer_names[p.manufacturer]], ['Variant', p => p.variant], ['Category', p => p.category], ['Size', p => p.size], ['Packaging', p => p.pack], ['Market', p => p.market]].map(([label, value]) => <tr key={label}><th scope="row">{label}</th>{comparison.map(p => <td key={p.id}>{value(p)}</td>)}</tr>)}
            <tr><th scope="row">Warnings and source limits</th>{comparison.map(p => <td key={p.id}><Restrictions product={p} /></td>)}</tr>
            <tr><th scope="row">Source and revision</th>{comparison.map(p => <td key={p.id}><ul>{p.source_ids.map(id => { const s = directory.sources.find(source => source.id === id); return <li key={id}><SourceLink id={id} /><p>{s.revision}</p><p>{s.currency_note}</p></li>; })}</ul></td>)}</tr>
          </tbody>
        </table>
      </div>}
      <ul className="manufacturer-results" aria-label="Manufacturer product results">
        {matches.map(p => <li key={p.id}>
          <div className="manufacturer-card-heading"><div><span className="manufacturer-brand">{directory.manufacturer_names[p.manufacturer]}</span><h3>{p.name}</h3></div>
            <label className="manufacturer-checkbox"><input type="checkbox" checked={selected.includes(p.id)} disabled={!selected.includes(p.id) && selected.length >= 4} onChange={() => toggle(p.id)} aria-label={`Compare ${p.name} ${p.sku}`} />Compare</label>
          </div>
          <p><strong>{p.sku}</strong> · {p.variant}</p><p>{p.size} · {p.pack}</p>
          <ProductSources product={p} />
        </li>)}
      </ul>
      {matches.length === 0 && <p>No exact records found. Try a manufacturer, product name or complete catalog number.</p>}
      <details className="manufacturer-gaps"><summary>Coverage and unresolved variants</summary>
        <p>This directory represents the listed catalog records, not each manufacturer’s complete portfolio. Source dates describe observations, not guaranteed current stock or labeling. Pricing and reimbursement do not establish suitability.</p>
        <ul>{directory.gaps.map(g => <li key={g.label}><strong>{directory.manufacturer_names[g.manufacturer]} · {g.label}.</strong> {g.reason}</li>)}</ul>
      </details>
    </section>}
  </details>;
}
